// Debug "View agent steps": renders one chat turn's full execution trace (conversation_runs.steps[]).
// Dark-themed to sit inline under an assistant bubble. `TurnSteps` is the container (toggle + lazy
// fetch by turn id); `AgentStepsView` is the pure timeline. Modeled on the platform SessionDebugger
// but for the step_type shape (user_input → llm_call → tool_call/tool_output → action → response).
import React, { useState } from 'react';
import { useGetConversationRunsQuery, type AgentRun, type AgentRunStep } from '@/redux/internal-services/agentApi';

type StepType = AgentRunStep['step_type'];

const STEP_META: Record<StepType, { label: string; badge: string }> = {
  user_input:     { label: 'User',      badge: 'bg-green-500/15 text-green-300 border-green-500/30' },
  llm_call:       { label: 'LLM call',  badge: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
  tool_call:      { label: 'Tool call', badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  tool_output:    { label: 'Tool result', badge: 'bg-teal-500/15 text-teal-300 border-teal-500/30' },
  agent_action:   { label: 'Action',    badge: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  ux_snapshot:    { label: 'UX',        badge: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
  agent_response: { label: 'Response',  badge: 'bg-ui-subtle text-ui-text2 border-ui-border' },
};

const asObj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? v as Record<string, unknown> : {});
const asStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : JSON.stringify(v));

function pretty(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') {
    try { return JSON.stringify(JSON.parse(v), null, 2); } catch { return v; }
  }
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}

function fmtTime(ts?: string): string {
  if (!ts) return '';
  try { return new Date(ts).toLocaleTimeString(); } catch { return ''; }
}

// One-line preview per step type.
function previewOf(step: AgentRunStep): string {
  const c = step.content;
  const m = step.metadata || {};
  switch (step.step_type) {
    case 'user_input':
    case 'agent_response':
      return asStr(c);
    case 'ux_snapshot':
      return asStr(m.caption) || asStr(c);
    case 'llm_call': {
      const obj = asObj(c);
      const tools = (asObj(obj.request_params).tools as unknown[]) || [];
      return `${asStr(obj.model) || 'model'} · ${tools.length} tool${tools.length === 1 ? '' : 's'}`;
    }
    case 'tool_call': {
      const obj = asObj(c);
      return `${asStr(obj.name) || 'tool'}(${asStr(obj.arguments)})`;
    }
    case 'tool_output': {
      const obj = asObj(c);
      return `${asStr(obj.name) || 'tool'} → ${asStr(obj.result)}`;
    }
    case 'agent_action':
      return [asStr(m.kind), asStr(m.module), asStr(m.caption) || asStr(m.title)].filter(Boolean).join(' · ');
    default:
      return asStr(c);
  }
}

const Pre: React.FC<{ children: string }> = ({ children }) => (
  <pre className="text-[11px] text-ui-text whitespace-pre-wrap font-mono bg-ui-subtle rounded border border-ui-border p-2 max-h-72 overflow-auto">
    {children || <span className="text-ui-text3 italic">empty</span>}
  </pre>
);

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-medium uppercase tracking-wide text-ui-text2">{children}</p>
);

// Expanded detail per step type.
const StepDetail: React.FC<{ step: AgentRunStep }> = ({ step }) => {
  const c = step.content;
  const m = step.metadata || {};
  if (step.step_type === 'llm_call') {
    const obj = asObj(c);
    const promptMsgs = (obj.prompt as { role?: string; content?: unknown }[]) || [];
    const tools = (asObj(obj.request_params).tools as unknown[]) || [];
    return (
      <div className="space-y-2">
        <p className="text-[11px] text-ui-text2">model: <span className="font-mono text-indigo-500 dark:text-indigo-300">{asStr(obj.model) || '—'}</span></p>
        {promptMsgs.map((pm, i) => (
          <div key={i} className="space-y-1">
            <Label>{pm.role || 'message'}</Label>
            <Pre>{asStr(pm.content)}</Pre>
          </div>
        ))}
        {tools.length > 0 && (<><Label>Tools ({tools.length})</Label><Pre>{pretty(tools)}</Pre></>)}
      </div>
    );
  }
  if (step.step_type === 'tool_call') {
    const obj = asObj(c);
    const resultRaw = m.result ?? m.output ?? m.tool_result;
    const statusColor = asStr(m.status) === 'success' ? 'text-green-500 dark:text-green-400'
      : asStr(m.status) === 'failed' ? 'text-red-500' : 'text-ui-text3';
    return (
      <div className="space-y-1">
        <p className="text-[11px] text-ui-text2">
          tool: <span className="font-mono text-amber-600 dark:text-amber-300">{asStr(obj.name) || '—'}</span>
          {m.status ? <span className={`ml-2 ${statusColor}`}>status: {asStr(m.status)}</span> : null}
          {m.error ? <span className="ml-2 text-red-500">error: {asStr(m.error)}</span> : null}
        </p>
        <Label>Arguments</Label>
        <Pre>{pretty(obj.arguments)}</Pre>
        {resultRaw != null && (<><Label>Result</Label><Pre>{pretty(resultRaw)}</Pre></>)}
      </div>
    );
  }
  if (step.step_type === 'tool_output') {
    const obj = asObj(c);
    const statusRaw = asStr(asObj(obj.result).status || asObj(c).status || '');
    const statusColor = statusRaw === 'success' ? 'text-green-500 dark:text-green-400'
      : statusRaw === 'failed' ? 'text-red-500' : '';
    return (
      <div className="space-y-1">
        <p className="text-[11px] text-ui-text2">
          tool: <span className="font-mono text-teal-600 dark:text-teal-300">{asStr(obj.name) || '—'}</span>
          {statusRaw && <span className={`ml-2 ${statusColor}`}>status: {statusRaw}</span>}
        </p>
        <Label>Result</Label>
        <Pre>{pretty(obj.result)}</Pre>
      </div>
    );
  }
  if (step.step_type === 'agent_action') {
    return (
      <div className="space-y-1">
        <Label>Action metadata</Label>
        <Pre>{pretty(m)}</Pre>
      </div>
    );
  }
  // text-y steps
  return <Pre>{asStr(c)}</Pre>;
};

const StepRow: React.FC<{ step: AgentRunStep; index: number }> = ({ step, index }) => {
  const [open, setOpen] = useState(false);
  const meta = STEP_META[step.step_type] ?? { label: step.step_type, badge: 'bg-ui-subtle text-ui-text2 border-ui-border' };
  return (
    <div className="border border-ui-border rounded-lg bg-ui-subtle overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-ui-hover transition">
        <span className="text-[10px] font-mono text-ui-text3 w-5 flex-shrink-0 text-right">{index}</span>
        <span className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded border ${meta.badge}`}>{meta.label}</span>
        <span className="flex-1 min-w-0 text-[11px] text-ui-text2 truncate">{previewOf(step)}</span>
        <span className="flex-shrink-0 text-[9px] text-ui-text3 font-mono">{fmtTime(step.timestamp)}</span>
        <span className={`flex-shrink-0 text-ui-text3 transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      </button>
      {open && <div className="px-2.5 pb-2.5 pt-1 border-t border-ui-border"><StepDetail step={step} /></div>}
    </div>
  );
};

export const AgentStepsView: React.FC<{
  steps: AgentRunStep[];
  targetIndex?: number;
  targetRef?: React.RefObject<HTMLDivElement | null>;
}> = ({ steps, targetIndex, targetRef }) => {
  if (!steps.length) return <p className="text-[11px] text-ui-text2 py-2">No steps recorded for this turn.</p>;
  return (
    <div className="space-y-1">
      {steps.map((s, i) => (
        <div
          key={i}
          ref={i === targetIndex ? targetRef : undefined}
          className={i === targetIndex ? 'ring-1 ring-brand-bright/60 rounded-lg' : ''}
        >
          <StepRow step={s} index={i + 1} />
        </div>
      ))}
    </div>
  );
};

// Inline per-turn container: a "View agent steps" toggle that lazily fetches the turn's run trace.
export const TurnSteps: React.FC<{ conversationId: string; turnId: string }> = ({ conversationId, turnId }) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useGetConversationRunsQuery(
    { conversationId, turnId },
    { skip: !open || !conversationId || !turnId },
  );
  const steps = ((data as AgentRun[] | undefined) || []).flatMap(r => r.steps || []);

  return (
    <div className="mt-1 ml-1">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 text-[11px] text-ui-text3 hover:text-ui-text2 transition"
      >
        <span className={`transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
        {open ? 'Hide agent steps' : 'View agent steps'}
      </button>
      {open && (
        <div className="mt-1.5 max-w-[85%]">
          {isFetching && <p className="text-[11px] text-ui-text2 py-1">Loading steps…</p>}
          {isError && <p className="text-[11px] text-red-500 py-1">Couldn't load steps for this turn.</p>}
          {!isFetching && !isError && <AgentStepsView steps={steps} />}
        </div>
      )}
    </div>
  );
};

export default AgentStepsView;
